import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { useToast } from '../hooks/use-toast';

const CartContext = createContext();

export function CartProvider({ children }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCartItems = useCallback(async () => {
    try {
      if (!supabase || !user) {
        setCartItems([]);
        return;
      }

      const { data, error } = await supabase
        .from('cart')
        .select(`
          *,
          product:products (
            id,
            name,
            price,
            image_url
          )
        `)
        .eq('user_id', user.id);

      if (error) throw error;
      setCartItems(data || []);
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchCartItems();
  }, [fetchCartItems]);

  const addToCart = async (productId, quantity = 1) => {
    try {
      if (!user) return false;

      // Fetch current product stock to check availability
      const { data: product, error: productError } = await supabase
        .from('products')
        .select('available_stock, total_stock')
        .eq('id', productId)
        .single();
        
      if (productError) throw productError;

      const currentStock = product.available_stock ?? product.total_stock ?? 0;
      
      // Check if item already exists in user's cart directly from DB to avoid stale state issues
      const { data: existingCartData, error: cartFetchError } = await supabase
        .from('cart')
        .select('*')
        .eq('user_id', user.id)
        .eq('product_id', productId)
        .maybeSingle();

      if (cartFetchError) throw cartFetchError;

      const totalDesiredQuantity = (existingCartData ? existingCartData.quantity : 0) + quantity;

      if (currentStock < totalDesiredQuantity) {
        toast({
          title: "Out of Stock",
          description: "This product is out of stock.",
          variant: "destructive",
        });
        return false;
      }

      if (existingCartData) {
        const { error } = await supabase
          .from('cart')
          .update({ quantity: existingCartData.quantity + quantity })
          .eq('id', existingCartData.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('cart')
          .insert({
            user_id: user.id,
            product_id: productId,
            quantity
          });
        if (error) throw error;
      }

      await fetchCartItems();
      return true; // Success
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast({
        title: "Cart Error",
        description: error.message || "Failed to add item to cart. Please try again.",
        variant: "destructive",
      });
      return false;
    }
  };

  const removeFromCart = async (cartItemId) => {
    try {
      const { error } = await supabase
        .from('cart')
        .delete()
        .eq('id', cartItemId);
      if (error) throw error;

      await fetchCartItems();
    } catch (error) {
      console.error('Error removing from cart:', error);
    }
  };

  const updateQuantity = async (cartItemId, newQuantity) => {
    if (newQuantity < 1) return;
    try {
      const existingItem = cartItems.find(item => item.id === cartItemId);
      if (!existingItem) return;

      const diff = newQuantity - existingItem.quantity;

      // If increasing quantity, check stock
      if (diff > 0) {
        const { data: product } = await supabase
          .from('products')
          .select('available_stock, total_stock')
          .eq('id', existingItem.product_id)
          .single();

        const currentStock = product?.available_stock ?? product?.total_stock ?? 0;
        if (currentStock < newQuantity) {
          toast({
            title: "Stock Limit Reached",
            description: `Cannot increase quantity. Only ${currentStock} items left in stock.`,
            variant: "destructive",
          });
          return;
        }
      }

      const { error } = await supabase
        .from('cart')
        .update({ quantity: newQuantity })
        .eq('id', cartItemId);
      if (error) throw error;
      
      await fetchCartItems();
    } catch (error) {
      console.error('Error updating quantity:', error);
      toast({
        title: "Update Failed",
        description: error.message || "Failed to update item quantity. Please try again.",
        variant: "destructive",
      });
    }
  };

  const clearCart = async () => {
    try {
      if (!user) return;

      const { error } = await supabase
        .from('cart')
        .delete()
        .eq('user_id', user.id);
      if (error) throw error;
      setCartItems([]);
    } catch (error) {
      console.error('Error clearing cart:', error);
    }
  };

  const clearCartOnCheckout = async () => {
    try {
      if (!user) return;
      const { error } = await supabase
        .from('cart')
        .delete()
        .eq('user_id', user.id);
      if (error) throw error;
      setCartItems([]);
    } catch (error) {
      console.error('Error clearing cart on checkout:', error);
    }
  };

  const cartTotal = cartItems.reduce((total, item) => {
    return total + (item.product?.price || 0) * item.quantity;
  }, 0);

  return (
    <CartContext.Provider value={{
      cartItems,
      loading,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      clearCartOnCheckout,
      cartTotal,
      refreshCart: fetchCartItems
    }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);

